export type GuestAttendanceRow = {
  id?: string | null;
  player_id?: string | null;
  display_name?: string | null;
  groupme_display_name?: string | null;
  rsvp_status?: string | null;
  guest_count?: number | null;
  players?: { first_name?: string | null; last_name?: string | null } | null;
};

export type GuestAttendanceEntry = {
  id: string;
  hostPlayerId: string | null;
  hostName: string;
  name: string;
  status: 'Going' | 'Maybe';
};

export function normalizedGuestCount(value: unknown, status?: string | null): number {
  if (status !== undefined && status !== 'Going' && status !== 'Maybe') return 0;
  const count = Number(value ?? 0);
  if (!Number.isInteger(count) || count < 0 || count > 10) return 0;
  return count;
}

export function guestHostName(row: GuestAttendanceRow): string {
  const playerName = `${row.players?.first_name || ''} ${row.players?.last_name || ''}`.trim();
  return playerName || row.display_name?.trim() || row.groupme_display_name?.trim() || 'Attendee';
}

export function eventGuestEntries(rows: GuestAttendanceRow[], statuses: Array<'Going' | 'Maybe'> = ['Going']): GuestAttendanceEntry[] {
  const uniqueHosts = new Map<string, GuestAttendanceRow>();
  rows.forEach((row, index) => {
    const key = row.player_id || row.id || `external:${guestHostName(row).toLowerCase()}:${index}`;
    if (!uniqueHosts.has(key)) uniqueHosts.set(key, row);
  });

  return [...uniqueHosts.entries()].flatMap(([key, row]) => {
    const status = row.rsvp_status === 'Maybe' ? 'Maybe' : 'Going';
    if (!statuses.includes(status) || row.rsvp_status !== status) return [];
    const hostName = guestHostName(row);
    return Array.from({ length: normalizedGuestCount(row.guest_count, status) }, (_, index) => ({
      id: `guest:${key}:${index + 1}`,
      hostPlayerId: row.player_id || null,
      hostName,
      name: `${hostName}'s Guest ${index + 1}`,
      status,
    }));
  });
}

export function summarizeEventGuests(rows: GuestAttendanceRow[]) {
  const uniqueHosts = new Map<string, GuestAttendanceRow>();
  rows.forEach((row, index) => {
    const key = row.player_id || row.id || `external:${guestHostName(row).toLowerCase()}:${index}`;
    if (!uniqueHosts.has(key)) uniqueHosts.set(key, row);
  });
  const uniqueRows = [...uniqueHosts.values()];
  const goingPlayers = uniqueRows.filter((row) => row.rsvp_status === 'Going').length;
  const maybePlayers = uniqueRows.filter((row) => row.rsvp_status === 'Maybe').length;
  const goingGuestEntries = eventGuestEntries(uniqueRows, ['Going']);
  const maybeGuestEntries = eventGuestEntries(uniqueRows, ['Maybe']);
  return {
    goingPlayers,
    guests: goingGuestEntries.length,
    totalAttending: goingPlayers + goingGuestEntries.length,
    maybePlayers,
    maybeGuests: maybeGuestEntries.length,
    potentialAdditional: maybePlayers + maybeGuestEntries.length,
    goingGuestEntries,
    maybeGuestEntries,
  };
}
